/**
 * Due Diligence Screen - MOBILE ONLY
 * 
 * Checklist-based evaluation system for opportunities.
 * Supports multiple modules, flag ratings, and progress tracking.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, RefreshControl } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';
import { StyleSheet } from 'react-native';
import { useDueDiligence } from '../../../hooks/useDueDiligence';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import {
  FLAG_CONFIG,
  STATUS_CONFIG,
  PHASE_CONFIG,
  MODULE_CONFIG,
  FlagType,
  ItemStatus,
  DueDiligenceItem,
  WorkflowPhase,
  getDefaultModules,
  BasePropertyType,
} from '../../../shared/types/due-diligence';
import { getChecklistItems, ALL_MODULES } from '../../../shared/due-diligence';

export default function DueDiligenceScreen() {
  const router = useRouter();
  const { portfolioId, opportunityId, opportunityName } = useLocalSearchParams<{
    portfolioId: string;
    opportunityId: string;
    opportunityName?: string;
  }>();

  const portId = portfolioId ? parseInt(portfolioId, 10) : 0;
  const oppId = opportunityId ? parseInt(opportunityId, 10) : 0;

  const {
    dueDiligence,
    items,
    loading,
    error,
    completionPercent,
    flagCounts,
    statusCounts,
    refresh,
    updateItem,
    initialize,
  } = useDueDiligence({ portfolioId: portId, opportunityId: oppId });

  const [selectedModule, setSelectedModule] = useState<string | 'all'>('all');
  const [refreshing, setRefreshing] = useState(false);

  const handleBack = useCallback(() => router.back(), [router]);
  
  // Setup checklist with default items for raw land
  const [setupLoading, setSetupLoading] = useState(false);
  const handleSetupChecklist = useCallback(async () => {
    const propertyType: BasePropertyType = 'raw_land'; // Default to raw land
    const modules = getDefaultModules(propertyType);
    
    // Build items array from module definitions
    const items: Array<{ code: string; module: string; category: string; title: string; description?: string; isGroupTask: boolean }> = [];
    modules.forEach(moduleCode => {
      const moduleDef = ALL_MODULES[moduleCode];
      if (moduleDef) {
        moduleDef.categories.forEach(cat => {
          cat.items.forEach(item => {
            items.push({
              code: item.code,
              module: moduleCode,
              category: cat.name,
              title: item.title,
              description: item.description,
              isGroupTask: item.isGroupTask || false,
            });
          });
        });
      }
    });
    
    setSetupLoading(true);
    const success = await initialize(propertyType, modules, items);
    setSetupLoading(false);
    
    if (!success) {
      // Error shown by hook
    }
  }, [initialize]);

  // Group items by module and category
  const groupedItems = useMemo(() => {
    const filtered = selectedModule === 'all' 
      ? items 
      : items.filter(item => item.module === selectedModule);

    const grouped: Record<string, Record<string, DueDiligenceItem[]>> = {};
    filtered.forEach(item => {
      if (!grouped[item.module]) grouped[item.module] = {};
      if (!grouped[item.module][item.category]) grouped[item.module][item.category] = [];
      grouped[item.module][item.category].push(item);
    });
    return grouped;
  }, [items, selectedModule]);

  // Get unique modules from items
  const modules = useMemo(() => {
    const moduleSet = new Set(items.map(item => item.module));
    return Array.from(moduleSet);
  }, [items]);

  // Toggle item status
  const handleToggleStatus = useCallback(async (item: DueDiligenceItem) => {
    const nextStatus: Record<ItemStatus, ItemStatus> = {
      pending: 'in_progress',
      in_progress: 'complete',
      complete: 'pending',
      not_applicable: 'pending',
    };
    await updateItem(item.id, { status: nextStatus[item.status] });
  }, [updateItem]);

  // Set item flag
  const handleSetFlag = useCallback(async (item: DueDiligenceItem, flag: FlagType | null) => {
    await updateItem(item.id, { flag: flag || undefined });
  }, [updateItem]);

  // Loading state
  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Due Diligence" onBack={handleBack} loading />
        <View style={styles.center}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  // Full-screen error only when no data has loaded yet (initial fetch failed)
  if (error && items.length === 0 && !dueDiligence) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Due Diligence" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={refresh} style={styles.retryButton}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </Pressable>
        </View>
      </ScreenContainer>
    );
  }

  // No items - show setup
  if (items.length === 0) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Due Diligence" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="checkbox-outline" size={icon.xxl} color={color.primary} />
          <Text style={styles.emptyTitle}>No Checklist Items</Text>
          <Text style={styles.emptySubtitle}>
            Configure the due diligence checklist for this opportunity
          </Text>
          <Pressable 
            style={[styles.setupButton, setupLoading && styles.setupButtonDisabled]} 
            onPress={handleSetupChecklist}
            disabled={setupLoading}
          >
            {setupLoading ? (
              <ActivityIndicator size="small" color={color.surface} />
            ) : (
              <Text style={styles.setupButtonText}>Setup Checklist</Text>
            )}
          </Pressable>
        </View>
      </ScreenContainer>
    );
  }

  const currentPhase = dueDiligence?.currentPhase || 'setup';
  const phaseConfig = PHASE_CONFIG[currentPhase as WorkflowPhase];

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Due Diligence'} onBack={handleBack} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await refresh(); setRefreshing(false); }} tintColor={color.primary} />}
      >
        {/* PROGRESS SUMMARY */}
        <View style={styles.summaryCard}>
          <View style={styles.progressRow}>
            <View style={styles.progressInfo}>
              <Text style={styles.progressPercent}>{completionPercent}%</Text>
              <Text style={styles.progressLabel}>Complete</Text>
            </View>
            <View style={styles.progressBarContainer}>
              <View style={[styles.progressBar, { width: `${completionPercent}%` }]} />
            </View>
          </View>

          {/* Phase indicator */}
          <View style={styles.phaseRow}>
            <Ionicons name="git-branch-outline" size={icon.sm} color={color.textMuted} />
            <Text style={styles.phaseText}>{phaseConfig?.label || 'Setup'}</Text>
          </View>

          {/* Flag summary */}
          <View style={styles.flagSummary}>
            {(['big_red', 'red', 'yellow', 'green', 'big_green'] as FlagType[]).map(flag => {
              const count = flagCounts?.[flag] ?? 0;
              if (count === 0) return null;
              const config = FLAG_CONFIG?.[flag];
              if (!config?.icon || !config?.color) return null;
              return (
                <View key={flag} style={styles.flagCount}>
                  <Ionicons name={config.icon as any} size={14} color={config.color} />
                  <Text style={[styles.flagCountText, { color: config.color }]}>{count}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* MODULE FILTER */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.moduleFilter}>
          <Pressable
            style={[styles.moduleChip, selectedModule === 'all' && styles.moduleChipActive]}
            onPress={() => setSelectedModule('all')}
          >
            <Text style={[styles.moduleChipText, selectedModule === 'all' && styles.moduleChipTextActive]}>
              All ({items.length})
            </Text>
          </Pressable>
          {modules.map(mod => {
            const config = MODULE_CONFIG[mod as keyof typeof MODULE_CONFIG];
            const count = items.filter(i => i.module === mod).length;
            return (
              <Pressable
                key={mod}
                style={[styles.moduleChip, selectedModule === mod && styles.moduleChipActive]}
                onPress={() => setSelectedModule(mod)}
              >
                <Ionicons
                  name={config?.icon as any || 'list-outline'}
                  size={14}
                  color={selectedModule === mod ? color.surface : color.textMuted}
                />
                <Text style={[styles.moduleChipText, selectedModule === mod && styles.moduleChipTextActive]}>
                  {config?.name || mod} ({count})
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* ITEMS BY MODULE/CATEGORY */}
        {Object.entries(groupedItems).map(([module, categories]) => (
          <View key={module} style={styles.moduleSection}>
            <Text style={styles.moduleName}>
              {MODULE_CONFIG[module as keyof typeof MODULE_CONFIG]?.name || module}
            </Text>

            {Object.entries(categories).map(([category, categoryItems]) => (
              <View key={category} style={styles.categorySection}>
                <Text style={styles.categoryName}>{category}</Text>
                
                {categoryItems.map(item => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    onToggleStatus={() => handleToggleStatus(item)}
                    onSetFlag={(flag) => handleSetFlag(item, flag)}
                  />
                ))}
              </View>
            ))}
          </View>
        ))}

        <View style={{ height: space.xxl }} />
      </ScrollView>
    </ScreenContainer>
  );
}

// Item row component
interface ItemRowProps {
  item: DueDiligenceItem;
  onToggleStatus: () => void;
  onSetFlag: (flag: FlagType | null) => void;
}

const DEFAULT_STATUS_CONFIG = { label: 'Pending', color: '#9CA3AF', icon: 'square-outline' };
const DEFAULT_FLAG_CONFIG = { label: 'None', color: '#9CA3AF', icon: 'flag-outline' };

function ItemRow({ item, onToggleStatus, onSetFlag }: ItemRowProps) {
  const [showFlags, setShowFlags] = useState(false);
  const statusConfig = (item?.status && STATUS_CONFIG?.[item.status]) || DEFAULT_STATUS_CONFIG;
  const flagConfig = (item?.flag && FLAG_CONFIG?.[item.flag]) || null;

  return (
    <View style={styles.itemRow}>
      <Pressable onPress={onToggleStatus} style={styles.itemCheckbox}>
        <Ionicons
          name={item.status === 'complete' ? 'checkbox' : item.status === 'in_progress' ? 'ellipse' : 'square-outline'}
          size={icon.md}
          color={statusConfig.color}
        />
      </Pressable>

      <View style={styles.itemContent}>
        <Text style={[styles.itemTitle, item.status === 'complete' && styles.itemTitleComplete]}>
          {item.title}
        </Text>
        {item.description && <Text style={styles.itemDescription}>{item.description}</Text>}
      </View>

      <Pressable onPress={() => setShowFlags(!showFlags)} style={styles.itemFlag}>
        {flagConfig ? (
          <Ionicons name={flagConfig.icon as any} size={icon.md} color={flagConfig.color} />
        ) : (
          <Ionicons name="flag-outline" size={icon.md} color={color.textMuted} />
        )}
      </Pressable>

      {/* Flag selector popup */}
      {showFlags && (
        <View style={styles.flagPopup}>
          {(['big_red', 'red', 'yellow', 'green', 'big_green', 'clear'] as FlagType[]).map(flag => {
            const config = FLAG_CONFIG[flag];
            if (!config) return null;
            return (
              <Pressable
                key={flag}
                style={styles.flagOption}
                onPress={() => {
                  onSetFlag(flag);
                  setShowFlags(false);
                }}
              >
                <Ionicons name={config.icon as any} size={16} color={config.color} />
              </Pressable>
            );
          })}
          <Pressable
            style={styles.flagOption}
            onPress={() => {
              onSetFlag(null);
              setShowFlags(false);
            }}
          >
            <Ionicons name="close" size={16} color={color.textMuted} />
          </Pressable>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { padding: space.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: space.xl },
  errorText: { fontSize: text.body.fontSize, color: color.error, marginTop: space.md, textAlign: 'center' },
  retryButton: { marginTop: space.lg, paddingHorizontal: space.lg, paddingVertical: space.sm, backgroundColor: color.primary, borderRadius: radius.md },
  retryButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight },
  emptyTitle: { fontSize: text.cardTitle.fontSize, fontWeight: text.cardTitle.fontWeight, color: color.textPrimary, marginTop: space.lg },
  emptySubtitle: { fontSize: text.body.fontSize, color: color.textMuted, marginTop: space.sm, textAlign: 'center' },
  setupButton: { marginTop: space.lg, paddingHorizontal: space.xl, paddingVertical: space.md, backgroundColor: color.primary, borderRadius: radius.md, minWidth: 160, alignItems: 'center' },
  setupButtonDisabled: { opacity: 0.6 },
  setupButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight, fontSize: text.body.fontSize },

  // Summary card
  summaryCard: { backgroundColor: color.backgroundSecondary, borderRadius: radius.lg, padding: space.md, marginBottom: space.md },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: space.md },
  progressInfo: { alignItems: 'center' },
  progressPercent: { fontSize: text.pageTitle.fontSize, fontWeight: text.pageTitle.fontWeight, color: color.primary },
  progressLabel: { fontSize: text.caption.fontSize, color: color.textMuted },
  progressBarContainer: { flex: 1, height: 8, backgroundColor: color.divider, borderRadius: radius.sm, overflow: 'hidden' },
  progressBar: { height: '100%', backgroundColor: color.primary, borderRadius: radius.sm },
  phaseRow: { flexDirection: 'row', alignItems: 'center', gap: space.xs, marginTop: space.sm },
  phaseText: { fontSize: text.caption.fontSize, color: color.textMuted },
  flagSummary: { flexDirection: 'row', gap: space.md, marginTop: space.sm },
  flagCount: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  flagCountText: { fontSize: text.caption.fontSize, fontWeight: text.bodyMedium.fontWeight },

  // Module filter
  moduleFilter: { marginBottom: space.md },
  moduleChip: { flexDirection: 'row', alignItems: 'center', gap: space.xs, paddingHorizontal: space.md, paddingVertical: space.sm, backgroundColor: color.backgroundSecondary, borderRadius: radius.full, marginRight: space.sm },
  moduleChipActive: { backgroundColor: color.primary },
  moduleChipText: { fontSize: text.caption.fontSize, color: color.textMuted },
  moduleChipTextActive: { color: color.surface },

  // Module section
  moduleSection: { marginBottom: space.lg },
  moduleName: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.textPrimary, marginBottom: space.sm },
  categorySection: { marginBottom: space.md },
  categoryName: { fontSize: text.caption.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textMuted, marginBottom: space.xs, textTransform: 'uppercase', letterSpacing: 0.5 },

  // Item row
  itemRow: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: color.surface, borderRadius: radius.md, padding: space.sm, marginBottom: space.xs, position: 'relative' },
  itemCheckbox: { padding: space.xs },
  itemContent: { flex: 1, paddingHorizontal: space.sm },
  itemTitle: { fontSize: text.body.fontSize, color: color.textPrimary },
  itemTitleComplete: { color: color.textMuted, textDecorationLine: 'line-through' },
  itemDescription: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: 2 },
  itemFlag: { padding: space.xs },

  // Flag popup
  flagPopup: { position: 'absolute', right: 0, top: 40, flexDirection: 'row', backgroundColor: color.surface, borderRadius: radius.md, padding: space.xs, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 4, elevation: 4, zIndex: 10 },
  flagOption: { padding: space.sm },
});

